<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import SectionContainer from "@/views/blocks/shared/section/container/SectionContainer.vue";
import type { WithStickyProductScreenshotProps } from "./WithStickyProductScreenshotProps";
import Feature from "@/views/blocks/shared/section/features/Feature.vue";
import Features from "@/views/blocks/shared/section/features/Features.vue";

// Import shared components
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";

const props = defineProps<{
    block: PageBlock<WithStickyProductScreenshotProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <SectionContainer
        :id="block.webId"
        class="relative isolate"
        :backgroundColor="styleValues.background"
    >
        <Image
            v-if="!dataValues.backgroundImage.isHidden"
            :src="dataValues.backgroundImage.data.src"
            :style="{
                opacity: dataValues.backgroundImage.data.transparency / 100,
            }"
            class="absolute inset-0 -z-10 size-full object-cover mix-blend-multiply"
            alt=""
        />
        <div
            class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10"
        >
            <div
                class="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8 px-6"
            >
                <div class="lg:pr-4">
                    <div class="lg:max-w-lg">
                        <EditableElement
                            v-if="!dataValues.tag.isHidden"
                            :blockId="block.id"
                            fieldName="tag"
                        >
                            <Heading
                                :text="dataValues.tag.data.text"
                                :tag="dataValues.tag.data.tag"
                                :textColor="styleValues.brand"
                                class="text-base/7 mb-2"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.title.isHidden"
                            :blockId="block.id"
                            fieldName="title"
                        >
                            <Heading
                                class="mt-2 text-4xl sm:text-5xl"
                                :text="dataValues.title.data.text"
                                :tag="dataValues.title.data.tag"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.description.isHidden"
                            :blockId="block.id"
                            fieldName="description"
                        >
                            <Paragraph
                                class="mt-6 text-lg/8 opacity-70"
                                :text="dataValues.description.data.text"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
            <div
                class="lg:sticky lg:top-24 lg:col-start-2 lg:row-span-2 lg:row-start-1 overflow-hidden pl-6"
            >
                <EditableElement
                    v-if="!dataValues.image.isHidden"
                    :blockId="block.id"
                    fieldName="image"
                >
                    <Image
                        class="w-[48rem] max-w-none rounded-xl shadow-xl sm:w-[57rem]"
                        :src="dataValues.image.data.src"
                        :alt="dataValues.image.data.alt"
                        width="912"
                        height="608"
                    />
                </EditableElement>
            </div>
            <div
                class="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8 px-6"
            >
                <div class="lg:pr-4">
                    <div class="max-w-xl text-base/7 lg:max-w-lg">
                        <EditableElement
                            v-if="!dataValues.topText.isHidden"
                            :blockId="block.id"
                            fieldName="topText"
                        >
                            <Paragraph
                                class="opacity-70"
                                :text="dataValues.topText.data.text"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                        <Features>
                            <EditableElement
                                v-for="feature in dataValues.features.data.items"
                                :key="feature.id"
                                :blockId="block.id"
                                :fieldName="`features_${feature.id}`"
                                :hideable="false"
                            >
                                <Feature
                                    :title="feature.title"
                                    :description="feature.description"
                                    :iconSrc="feature.iconSrc"
                                    :titleTextColor="styleValues.text"
                                    :descriptionTextColor="styleValues.text"
                                />
                            </EditableElement>
                        </Features>
                        <EditableElement
                            v-if="!dataValues.bottomText.isHidden"
                            :blockId="block.id"
                            fieldName="bottomText"
                        >
                            <Paragraph
                                class="mt-8 opacity-70"
                                :text="dataValues.bottomText.data.text"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.secondaryTitle.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryTitle"
                        >
                            <Heading
                                class="mt-16 text-2xl"
                                :text="dataValues.secondaryTitle.data.text"
                                :tag="dataValues.secondaryTitle.data.tag"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.secondaryDescription.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryDescription"
                        >
                            <Paragraph
                                class="mt-6 text-lg/8 opacity-70"
                                :text="dataValues.secondaryDescription.data.text"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
        </div>
    </SectionContainer>
</template>
